import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { PromptVoicePreview as PreviewType } from "../../../api/promptVoice";
import styles from "./PromptVoicePreview.module.css";

interface PromptVoicePreviewProps {
    previews: PreviewType[];
    onSelect: (preview: PreviewType) => void;
    onBack: () => void;
}

const PromptVoicePreview: React.FC<PromptVoicePreviewProps> = ({ previews, onSelect, onBack }) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;

    const [playingId, setPlayingId] = useState<string | null>(null);
    const audioRefs = useRef<{ [key: string]: HTMLAudioElement | null }>({});

    // Cleanup audio elements on unmount
    useEffect(() => {
        // Capture ref value inside effect to avoid stale ref in cleanup
        const audioElements = audioRefs.current;
        return () => {
            Object.values(audioElements).forEach((audio) => {
                if (audio) {
                    try {
                        audio.pause();
                        audio.src = "";
                        audio.onended = null;
                        audio.onerror = null;
                    } catch {
                        // Ignore cleanup errors
                    }
                }
            });
        };
    }, []);

    // Stop audio when selecting a preview
    const stopAllAudio = () => {
        Object.values(audioRefs.current).forEach((audio) => {
            if (audio) {
                try {
                    audio.pause();
                } catch {
                    // Ignore errors
                }
            }
        });
        setPlayingId(null);
    };

    const handlePlay = (preview: PreviewType) => {
        // Stop any currently playing audio
        if (playingId && audioRefs.current[playingId]) {
            audioRefs.current[playingId]?.pause();
        }

        if (playingId === preview.id) {
            // Toggle off
            setPlayingId(null);
            return;
        }

        // Create audio element from base64 if not exists
        if (!audioRefs.current[preview.id] && preview.audioBase64) {
            const audio = new Audio(`data:audio/mpeg;base64,${preview.audioBase64}`);
            audio.onended = () => setPlayingId(null);
            audio.onerror = () => {
                console.error("Error playing audio");
                setPlayingId(null);
            };
            audioRefs.current[preview.id] = audio;
        }

        const audio = audioRefs.current[preview.id];
        if (audio) {
            audio.currentTime = 0;
            audio.play().catch((err) => {
                console.error("Error playing audio:", err);
                setPlayingId(null);
            });
            setPlayingId(preview.id);
        }
    };

    const formatDuration = (seconds: number | null): string => {
        if (!seconds) return "--:--";
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, "0")}`;
    };

    return (
        <div className={styles.previewSection}>
            <h3>{t("selectVoicePreview")}</h3>
            <p className={styles.previewInstructions}>{t("selectVoicePreviewInstructions")}</p>

            <div className={styles.previewGrid}>
                {previews.map((preview, index) => (
                    <div key={preview.id} className={styles.previewCard}>
                        <div className={styles.previewHeader}>
                            <span className={styles.previewNumber}>
                                {t("voice")} {index + 1}
                            </span>
                            <span className={styles.previewDuration}>{formatDuration(preview.duration)}</span>
                        </div>

                        <button
                            className={`${styles.playButton} ${playingId === preview.id ? styles.playing : ""}`}
                            onClick={() => handlePlay(preview)}
                            disabled={!preview.audioBase64}
                        >
                            {playingId === preview.id ? t("stop") : t("play")}
                        </button>

                        <button
                            className={styles.selectButton}
                            onClick={() => {
                                stopAllAudio();
                                onSelect(preview);
                            }}
                        >
                            {t("selectThisVoice")}
                        </button>
                    </div>
                ))}
            </div>

            <div className={styles.buttonGroup}>
                <button
                    className={styles.secondaryButton}
                    onClick={() => {
                        stopAllAudio();
                        onBack();
                    }}
                >
                    {t("generateNewPreviews")}
                </button>
            </div>
        </div>
    );
};

export default PromptVoicePreview;
